/**
 * Stripe Payment Form — deferred intent flow
 *
 * Card form renders immediately without a clientSecret (using mode: 'payment').
 * On submit: validate card -> create order on backend -> confirm payment.
 */

import { useState, useRef } from 'react'
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { Lock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui'

interface StripePaymentFormProps {
  onCreateOrder: () => Promise<{ clientSecret: string; orderId?: string } | null>
  onSuccess: () => void
  onError: (message: string) => void
  isProcessing: boolean
  setIsProcessing: (v: boolean) => void
  total: number
  currency?: string
  disabled?: boolean
  isCreatingOrder?: boolean
  checkoutStep?: string
}

export function StripePaymentForm({
  onCreateOrder,
  onSuccess,
  onError,
  isProcessing,
  setIsProcessing,
  total,
  currency = 'USD',
  disabled,
  isCreatingOrder,
  checkoutStep,
}: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const submittingRef = useRef(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements || submittingRef.current) return

    submittingRef.current = true
    setIsProcessing(true)
    setPaymentError(null)

    // Step 1: Validate card details
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setPaymentError(submitError.message || 'Please check your card details.')
      submittingRef.current = false
      setIsProcessing(false)
      return
    }

    // Step 2: Create order on backend -> get clientSecret
    const result = await onCreateOrder()
    if (!result?.clientSecret) {
      setPaymentError('Could not create order. Please try again.')
      submittingRef.current = false
      setIsProcessing(false)
      return
    }

    // Step 3: Confirm payment with Stripe
    const returnUrl = window.location.origin + '/tracking' + (result.orderId ? `?order_id=${result.orderId}` : '')
    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: result.clientSecret,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    })

    if (error) {
      const message = error.message || 'Payment failed. Please try again.'
      setPaymentError(message)
      onError(message)
      submittingRef.current = false
      setIsProcessing(false)
    } else {
      onSuccess()
    }
  }

  const formattedTotal = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(total / 100)

  const showCreatingSteps = isCreatingOrder && checkoutStep

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Payment</h2>
        <p className="text-sm text-muted-foreground flex items-center gap-1">
          <Lock className="h-3.5 w-3.5" />
          Secure payment via Stripe
        </p>
      </div>

      <div className="border rounded-lg p-4">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {paymentError && (
        <p className="text-sm text-destructive">{paymentError}</p>
      )}

      <Button
        type="submit"
        variant="black"
        size="lg"
        disabled={!stripe || !elements || isProcessing || disabled || isCreatingOrder}
        className="w-full"
      >
        {showCreatingSteps ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {checkoutStep === 'authenticating' && 'Signing in...'}
            {checkoutStep === 'syncing-cart' && 'Syncing cart...'}
            {checkoutStep === 'validating' && 'Validating order...'}
            {checkoutStep === 'processing' && 'Creating order...'}
          </>
        ) : isProcessing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing payment...
          </>
        ) : (
          `Pay ${formattedTotal}`
        )}
      </Button>
    </form>
  )
}
